import Flex from '@react-css/flex';
import Grid from '@react-css/grid';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import useAlertQueue from 'hooks/useAlertQueue';
import useOnboardingAttributeParams from 'hooks/useOnboardingAttributeParams';
import useShopifyAppInstallAlert from 'hooks/useShopifyAppInstallAlert';
import useShopifyAppInstallOnboardingAttributes from 'hooks/useShopifyAppInstallOnboardingAttributes';
import qs from 'qs';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import { selectUser } from 'store/selectors/me/user';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Icon from 'storybookConfig/stories/molecules/Icon';
import Input from 'storybookConfig/stories/molecules/Input';
import TextAreaInput from 'storybookConfig/stories/molecules/Input/TextAreaInput';
import LegalConsent from 'storybookConfig/stories/molecules/LegalConsent';
import Link from 'storybookConfig/stories/molecules/Link';
import Form from 'storybookConfig/stories/organisms/Form';
import AuthLayout, {
  AuthCard,
  AuthCardTitle,
  AuthLede,
  AuthReveal,
  AuthStatement,
  AuthTitle,
} from 'storybookConfig/stories/species/Layout/AuthLayout';
import type ApiError from 'utils/ApiError';
import { signup, type SignupParams } from 'utils/api/signup';

const USE_CASES = [
  {
    icon: 'building',
    title: 'For retailers',
    text: 'Use Carro to onboard and integrate your dropship suppliers.',
  },
  {
    icon: 'store',
    title: 'For suppliers',
    text: 'Use Carro to connect with and sell through your retail partners.',
  },
] as const;

const DefaultContent = () => {
  return (
    <Flex column gap="64px">
      <Flex column gap="24px">
        <AuthReveal step={0}>
          <AuthStatement>Helping retailers and their suppliers trade more profitably</AuthStatement>
        </AuthReveal>

        <AuthReveal step={1}>
          <AuthLede>
            Carro&apos;s platform offers unique use cases for retailers and brands to help them
            transact with each other. Find yours below.
          </AuthLede>
        </AuthReveal>
      </Flex>

      <Grid columns="1fr 1fr" gap="24px">
        {USE_CASES.map(({ icon, title, text }, i) => (
          <AuthReveal key={title} step={2 + i * 1.5} motion="drop">
            <AuthCard>
              <Icon name={icon} color="bodyTextPrimary" size="24px" />
              <AuthCardTitle>{title}</AuthCardTitle>
              <Body color="bodyTextSecondary">{text}</Body>
            </AuthCard>
          </AuthReveal>
        ))}
      </Grid>
    </Flex>
  );
};

const SellerSignupContent = () => {
  return (
    <Flex column gap="64px">
      <Flex column gap="24px">
        <AuthReveal step={0}>
          <AuthStatement>Start selling your products with seamless integrations</AuthStatement>
        </AuthReveal>

        <AuthReveal step={1}>
          <AuthLede>
            We are a Supplier Enablement Platform to help you trade and grow your business with
            ease.
          </AuthLede>
        </AuthReveal>
      </Flex>

      <Flex column gap="24px">
        <AuthReveal step={2}>
          <Flex gap="16px">
            <Icon name="circle-check" color="bodyTextOnDark" size="24px" />
            <Heading variant="Headings/H4" color="bodyTextOnDark" as="span">
              Self Serve: Create account and start selling through any online store (B2C and B2B)
            </Heading>
          </Flex>
        </AuthReveal>

        <AuthReveal step={3}>
          <Flex gap="16px">
            <Icon name="circle-check" color="bodyTextOnDark" size="24px" />
            <Heading variant="Headings/H4" color="bodyTextOnDark" as="span">
              Manage orders and inventory from your existing systems
            </Heading>
          </Flex>
        </AuthReveal>

        <AuthReveal step={4}>
          <Flex gap="16px">
            <Icon name="circle-check" color="bodyTextOnDark" size="24px" />
            <Heading variant="Headings/H4" color="bodyTextOnDark" as="span">
              One-click integration with your e-commerce platform
            </Heading>
          </Flex>
        </AuthReveal>

        <AuthReveal step={5}>
          <Flex gap="16px">
            <Icon name="circle-check" color="bodyTextOnDark" size="24px" />
            <Heading variant="Headings/H4" color="bodyTextOnDark" as="span">
              Secured by SOC2 Type II compliant best practices
            </Heading>
          </Flex>
        </AuthReveal>
      </Flex>
    </Flex>
  );
};

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = useAppSelector(selectUser);
  const alertQueue = useAlertQueue();
  const onboardingAttributes = useOnboardingAttributeParams();

  useShopifyAppInstallAlert();
  useShopifyAppInstallOnboardingAttributes();

  /**
   * Helpers
   */

  const { email, redirect, invite, commerceType } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const isSellerSigningUp = commerceType === 'seller';

  /**
   * Mutations
   */

  const signingUp = useMutation({
    mutationFn: (params: SignupParams) => signup(params),
    onSuccess: (_, variables) => {
      navigate('/signin/success', { state: { email: variables.email, backPath: '/signup' } });
    },
    onError: (error: ApiError) => {
      alertQueue.addErrorAlert('Something went wrong', error.message);
      Sentry.captureException(error);
    },
  });

  /**
   * Form
   */

  interface FormFields {
    email: string;
    howDidYouHear?: string;
    hopeToAccomplish: string;
  }

  const { register, handleSubmit, formState } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      email: (email || '') as string,
      howDidYouHear: '',
      hopeToAccomplish: '',
    },
  });

  /**
   * Handlers
   */

  const onSubmit = handleSubmit((data: FormFields) => {
    const params: SignupParams = {
      email: data.email.toLowerCase().trim(),
      redirect: (redirect || '') as string,
      onboardingAttributes,
    };

    signingUp.mutate(params);
  });

  /**
   * Render
   */

  if (user) return <Navigate to="/" />;

  return (
    <AuthLayout
      side="right"
      palette="midnight"
      aside={isSellerSigningUp ? <SellerSignupContent /> : <DefaultContent />}
    >
      <Flex column gap="32px">
        <Flex column gap="16px">
          <AuthTitle>Grow your business, not your inventory</AuthTitle>
          <AuthLede color="bodyTextSecondary">
            {isSellerSigningUp
              ? 'Create your free account and start selling through retail partners.'
              : 'Create your Carro account and start sourcing from dropship suppliers.'}
          </AuthLede>
        </Flex>

        <Form onSubmit={onSubmit}>
          <Form.FieldGroup>
            <Form.Field
              labelText="Email"
              id="signup-email"
              errorText={formState.errors.email?.message}
              isRequired
            >
              <Input
                type="email"
                {...register('email', { required: 'Email is required' })}
                isFullWidth
                placeholder="email@example.com"
              />
            </Form.Field>

            <Form.Field
              labelText="How did you hear about us?"
              id="howDidYouHear"
              errorText={formState.errors.howDidYouHear?.message}
            >
              <Input
                {...register('howDidYouHear')}
                isFullWidth
                placeholder="A partner told me, G2, Shopify, Blog Post, etc"
              />
            </Form.Field>

            <Form.Field
              labelText="What do you hope to accomplish with Carro?"
              id="hopeToAccomplish"
              errorText={formState.errors.hopeToAccomplish?.message}
              isRequired
            >
              <TextAreaInput
                {...register('hopeToAccomplish', { required: 'This field is required' })}
                as="textarea"
                isFullWidth
                placeholder="E.g. I want to automate my order process"
              />
            </Form.Field>
          </Form.FieldGroup>

          <Form.Footer column gap="16px">
            <LegalConsent />

            <PrimaryButton type="submit" disabled={signingUp.isLoading}>
              Sign Up
            </PrimaryButton>

            <Body>
              Already have an account? <Link to="/signin">Sign in</Link>
            </Body>
          </Form.Footer>

          <Form.HiddenField name="invite" value={invite as string} />
          <Form.HiddenField name="source" value={onboardingAttributes.source} />
          <Form.HiddenField name="commerceType" value={onboardingAttributes.commerceType} />
          <Form.HiddenField name="reCompanyId" value={onboardingAttributes.referralCompanyId} />
        </Form>
      </Flex>
    </AuthLayout>
  );
};

export default SignUpPage;
